import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { useKFlags } from '@/hooks/use-k-flags';
import { replaceSurveySpanWithPlaceholder } from '@/utils/surveyFormatter';
import { FRAUD_DETECTOR_API } from '@/pages/settings/ats/FraudDetector/api';
import { FRAUD_DETECTOR_JOB_API } from '../api/fraudDetector.api';
import type { CBVJobSettings } from '../api/fraudDetector.api';
import {
  FRAUD_DETECTOR_FORM_DEFAULTS,
  type FraudDetectorFormValues,
} from '../types/fraudDetector.types';
import {
  useSaveFraudDetectorSettings,
  useDeleteFraudDetectorSettings,
} from '../helpers/fraudDetectorQueries';

export const fraudDetectorTeammateKeys = {
  accountSettings: ['fraudDetector', 'settings'] as const,
  jobSettings: (jobId: string | number) =>
    ['fraudDetector', 'jobSettings', jobId] as const,
};

function mapSettingsToFormValues(
  settings: CBVJobSettings,
  fromUsers: any[]
): FraudDetectorFormValues {
  const senderId =
    settings.contact_background_verification_auto_reject_email_sender_id;
  const sharedEmailId =
    settings.contact_background_verification_auto_reject_ats_shared_email_id;
  const surveyFormTemplateId =
    settings.contact_background_verification_auto_reject_survey_form_template_id;

  let fromUserObject = null;
  if (sharedEmailId) {
    fromUserObject =
      fromUsers.find((u) => u.isSharedEmail && u.id === sharedEmailId) || null;
  } else if (senderId) {
    fromUserObject =
      fromUsers.find((u) => !u.isSharedEmail && u.id === senderId) || null;
  }

  const rejectionReasonId =
    settings.contact_background_verification_auto_reject_ats_rejection_reason_id ??
    null;

  return {
    detection_mode:
      settings.contact_background_verification_auto_verification_enabled
        ? 'automatic'
        : 'manual',
    auto_reject_enabled:
      settings.contact_background_verification_auto_reject_enabled ?? false,
    ats_rejection_reason_id: rejectionReasonId,
    sendRejectEmail:
      settings.contact_background_verification_auto_reject_send_email ?? false,
    selectedTemplate:
      settings.contact_background_verification_auto_reject_ats_comm_template_id
        ? {
            id: settings.contact_background_verification_auto_reject_ats_comm_template_id,
          }
        : null,
    from: fromUserObject?.email || '',
    fromUserObject,
    subject:
      settings.contact_background_verification_auto_reject_email_subject ?? '',
    body: settings.contact_background_verification_auto_reject_email_body ?? '',
    cc: settings.contact_background_verification_auto_reject_cc ?? [],
    bcc: settings.contact_background_verification_auto_reject_bcc ?? [],
    attachments:
      settings.contact_background_verification_auto_reject_email_attachments ??
      [],
    sendAfter:
      settings.contact_background_verification_auto_reject_send_after ?? 1,
    scheduleEmail:
      settings.contact_background_verification_auto_reject_schedule ?? false,
    scheduleTime:
      settings.contact_background_verification_auto_reject_scheduled_at ?? null,
    scheduleTimezone:
      settings.contact_background_verification_auto_reject_scheduled_at_timezone ??
      null,
    survey: surveyFormTemplateId ? { value: surveyFormTemplateId } : null,
  };
}

function mapFormValuesToPayload(
  jobId: string | number,
  values: FraudDetectorFormValues,
  feSurvey: boolean,
  currentUserId: number | undefined
): Partial<CBVJobSettings> {
  const autoVerification = values.detection_mode === 'automatic';
  const templateObj = values.selectedTemplate as { id?: number } | null;
  const fromUserObj = values.fromUserObject as {
    id?: number;
    isSharedEmail?: boolean;
  } | null;
  const surveyObj = values.survey as { value?: number } | null;

  const payload: Partial<CBVJobSettings> = {
    ats_job_id: Number(jobId),
    contact_background_verification_integration_enabled: true,
    contact_background_verification_auto_verification_enabled: autoVerification,
    contact_background_verification_auto_reject_enabled:
      values.auto_reject_enabled,
  };

  if (!values.auto_reject_enabled) {
    payload.contact_background_verification_auto_reject_ats_rejection_reason_id =
      null;
    payload.contact_background_verification_auto_reject_send_email = false;
    payload.contact_background_verification_auto_reject_ats_form_template_id =
      null;
    payload.contact_background_verification_auto_reject_survey_form_template_id =
      null;
    payload.contact_background_verification_auto_reject_ats_comm_template_id =
      null;
    payload.contact_background_verification_auto_reject_email_subject = null;
    payload.contact_background_verification_auto_reject_email_body = null;
    payload.contact_background_verification_auto_reject_email_sender_id = null;
    payload.contact_background_verification_auto_reject_ats_shared_email_id =
      null;
    payload.contact_background_verification_auto_reject_cc = [];
    payload.contact_background_verification_auto_reject_bcc = [];
    payload.contact_background_verification_auto_reject_email_attachments = [];
    payload.contact_background_verification_auto_reject_schedule = false;
    payload.contact_background_verification_auto_reject_send_after = null;
    payload.contact_background_verification_auto_reject_scheduled_at = null;
    payload.contact_background_verification_auto_reject_scheduled_at_timezone =
      null;
    return payload;
  }

  payload.contact_background_verification_auto_reject_ats_rejection_reason_id =
    values.ats_rejection_reason_id;
  payload.contact_background_verification_auto_reject_send_email =
    values.sendRejectEmail;

  if (!values.sendRejectEmail) {
    payload.contact_background_verification_auto_reject_ats_form_template_id =
      null;
    payload.contact_background_verification_auto_reject_survey_form_template_id =
      null;
    payload.contact_background_verification_auto_reject_ats_comm_template_id =
      null;
    payload.contact_background_verification_auto_reject_email_subject = null;
    payload.contact_background_verification_auto_reject_email_body = null;
    payload.contact_background_verification_auto_reject_email_sender_id = null;
    payload.contact_background_verification_auto_reject_ats_shared_email_id =
      null;
    payload.contact_background_verification_auto_reject_cc = [];
    payload.contact_background_verification_auto_reject_bcc = [];
    payload.contact_background_verification_auto_reject_email_attachments = [];
    payload.contact_background_verification_auto_reject_schedule = false;
    payload.contact_background_verification_auto_reject_send_after = null;
    payload.contact_background_verification_auto_reject_scheduled_at = null;
    payload.contact_background_verification_auto_reject_scheduled_at_timezone =
      null;
    return payload;
  }

  const emailBody = feSurvey
    ? replaceSurveySpanWithPlaceholder(values.body)
    : values.body;

  payload.contact_background_verification_auto_reject_ats_comm_template_id =
    templateObj?.id ?? null;
  payload.contact_background_verification_auto_reject_email_subject =
    values.subject || null;
  payload.contact_background_verification_auto_reject_email_body =
    emailBody || null;

  if (feSurvey && surveyObj?.value) {
    payload.contact_background_verification_auto_reject_ats_form_template_id =
      surveyObj.value;
    payload.contact_background_verification_auto_reject_survey_form_template_id =
      surveyObj.value;
  } else {
    payload.contact_background_verification_auto_reject_ats_form_template_id =
      null;
    payload.contact_background_verification_auto_reject_survey_form_template_id =
      null;
  }

  if (fromUserObj?.isSharedEmail) {
    payload.contact_background_verification_auto_reject_ats_shared_email_id =
      fromUserObj.id;
    payload.contact_background_verification_auto_reject_email_sender_id =
      currentUserId ?? null;
  } else {
    payload.contact_background_verification_auto_reject_email_sender_id =
      fromUserObj?.id ?? null;
    payload.contact_background_verification_auto_reject_ats_shared_email_id =
      null;
  }

  payload.contact_background_verification_auto_reject_cc = values.cc ?? [];
  payload.contact_background_verification_auto_reject_bcc = values.bcc ?? [];
  payload.contact_background_verification_auto_reject_email_attachments =
    values.attachments ?? [];
  payload.contact_background_verification_auto_reject_schedule =
    values.scheduleEmail;

  if (values.scheduleEmail) {
    const timezoneKey =
      values.scheduleTimezone !== null &&
      typeof values.scheduleTimezone === 'object' &&
      'key' in (values.scheduleTimezone as object)
        ? (values.scheduleTimezone as { key: string }).key
        : (values.scheduleTimezone as string | null);

    payload.contact_background_verification_auto_reject_send_after =
      values.sendAfter || null;
    payload.contact_background_verification_auto_reject_scheduled_at =
      values.scheduleTime;
    payload.contact_background_verification_auto_reject_scheduled_at_timezone =
      timezoneKey;
  } else {
    payload.contact_background_verification_auto_reject_send_after = null;
    payload.contact_background_verification_auto_reject_scheduled_at = null;
    payload.contact_background_verification_auto_reject_scheduled_at_timezone =
      null;
  }

  return payload;
}

function checkFormValid(values: FraudDetectorFormValues): boolean {
  if (!values.auto_reject_enabled) return true;
  if (!values.sendRejectEmail) return true;
  const template = values.selectedTemplate as { id?: number } | null;
  if (!template?.id) return false;
  if (!values.from) return false;
  if (!values.subject) return false;
  return true;
}

export const useFraudDetectorTeammate = (jobId: string | number) => {
  const formRef = useRef<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [observedFormValues, setObservedFormValues] =
    useState<FraudDetectorFormValues | null>(null);
  const { fromUsers } = useGetAllUsers();
  const { feSurvey } = useKFlags();
  const { currUser } = useAuth();

  const { data: accountSettings, isLoading: isAccountSettingsLoading } =
    useQuery({
      queryKey: fraudDetectorTeammateKeys.accountSettings,
      queryFn: FRAUD_DETECTOR_API.getSettings,
      staleTime: 0,
    });

  const { data: jobSettings, isLoading: isJobSettingsLoading } = useQuery({
    queryKey: fraudDetectorTeammateKeys.jobSettings(jobId),
    queryFn: () => FRAUD_DETECTOR_JOB_API.getJobSettings(jobId),
    enabled: !!jobId && jobId !== 'draft',
    staleTime: 0,
  });

  const { mutate: deleteSettings, isPending: isDeleting } =
    useDeleteFraudDetectorSettings(jobId, () => setIsDeleteConfirmOpen(false));

  const { mutateAsync: saveSettings, isPending: isSaving } =
    useSaveFraudDetectorSettings(
      jobId,
      mapFormValuesToPayload,
      feSurvey,
      currUser?.id,
      () => setIsDrawerOpen(false)
    );

  const isEnabled = accountSettings?.cbv_integration_enabled ?? false;

  const isAdded =
    jobSettings?.contact_background_verification_integration_enabled ?? false;

  const initialValues = useMemo<FraudDetectorFormValues>(() => {
    if (!jobSettings) return FRAUD_DETECTOR_FORM_DEFAULTS;
    return mapSettingsToFormValues(jobSettings, fromUsers);
  }, [jobSettings, fromUsers]);

  const isSaveDisabled = useMemo(
    () => !checkFormValid(observedFormValues ?? initialValues),
    [observedFormValues, initialValues]
  );

  const handleFormValuesChange = useCallback(
    (values: FraudDetectorFormValues) => {
      setObservedFormValues(values);
    },
    []
  );

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const openDeleteConfirm = useCallback(() => setIsDeleteConfirmOpen(true), []);
  const closeDeleteConfirm = useCallback(
    () => setIsDeleteConfirmOpen(false),
    []
  );
  const handleDelete = useCallback(() => deleteSettings(), [deleteSettings]);

  const handleSave = useCallback(async () => {
    if (!formRef.current) return;
    const errors = await formRef.current.validateForm();
    if (Object.keys(errors).length > 0) {
      formRef.current.setTouched(
        Object.keys(errors).reduce(
          (acc: Record<string, boolean>, key) => ({ ...acc, [key]: true }),
          {}
        )
      );
      return;
    }
    await saveSettings(formRef.current.values);
  }, [saveSettings]);

  const isLoading = isAccountSettingsLoading || isJobSettingsLoading;

  const fraudDetectorModeLabel = isAdded
    ? jobSettings?.contact_background_verification_auto_verification_enabled
      ? 'Automatic mode'
      : 'Manual mode'
    : null;

  return {
    isEnabled,
    isAdded,
    fraudDetectorModeLabel,
    isLoading,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    isDeleteConfirmOpen,
    openDeleteConfirm,
    closeDeleteConfirm,
    handleDelete,
    isDeleting,
    initialValues,
    handleSave,
    isSaving,
    isSaveDisabled,
    handleFormValuesChange,
    formRef,
  };
};
